import React from "react";
import { X, Clock, User, Database, Globe } from "lucide-react";
import { AuditLog } from "../../api/services/auditLogs/types";
import { formatDateTime } from "../../utils/formatters";

interface AuditLogDetailsProps {
  log: AuditLog;
  onClose: () => void;
}

export const AuditLogDetails: React.FC<AuditLogDetailsProps> = ({
  log,
  onClose,
}) => {
  const renderJsonData = (data: Record<string, unknown> | null | undefined) => {
    if (!data) return <span className="text-gray-500">No data</span>;

    try {
      return (
        <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto">
          {JSON.stringify(data, null, 2)}
        </pre>
      );
    } catch {
      return <span className="text-gray-500">Invalid data</span>;
    }
  };

  const renderChangedFields = () => {
    if (!log.changed_fields || Object.keys(log.changed_fields).length === 0) {
      return <span className="text-gray-500">No fields changed</span>;
    }

    return (
      <div className="space-y-2">
        {Object.entries(log.changed_fields).map(([field, value]) => (
          <div key={field} className="flex items-start">
            <span className="font-medium text-gray-700 w-1/3">{field}:</span>
            <span className="text-gray-900 w-2/3">
              {typeof value === "object"
                ? JSON.stringify(value)
                : String(value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full sm:p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Audit Log Details
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-4">
            {/* Basic Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">
                Basic Information
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-start">
                  <Clock className="h-4 w-4 text-gray-400 mt-0.5 mr-2" />
                  <div>
                    <p className="text-sm text-gray-600">Timestamp</p>
                    <p className="text-sm font-medium text-gray-900">
                      {formatDateTime(log.action_timestamp)}
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Database className="h-4 w-4 text-gray-400 mt-0.5 mr-2" />
                  <div>
                    <p className="text-sm text-gray-600">Table</p>
                    <p className="text-sm font-medium text-gray-900">
                      {log.table_name}
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <User className="h-4 w-4 text-gray-400 mt-0.5 mr-2" />
                  <div>
                    <p className="text-sm text-gray-600">User</p>
                    <p className="text-sm font-medium text-gray-900">
                      {log.user_name || "System"}
                    </p>
                    {log.user_email && (
                      <p className="text-xs text-gray-500">{log.user_email}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-start">
                  <Globe className="h-4 w-4 text-gray-400 mt-0.5 mr-2" />
                  <div>
                    <p className="text-sm text-gray-600">Client IP</p>
                    <p className="text-sm font-medium text-gray-900">
                      {log.client_ip || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Details */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">Action Details</h4>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Action Type</p>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      log.action === "INSERT"
                        ? "bg-green-100 text-green-800"
                        : log.action === "UPDATE"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-red-100 text-red-800"
                    }`}
                  >
                    {log.action}
                  </span>
                </div>
                {log.record_id && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Record ID</p>
                    <p className="text-sm font-mono text-gray-900">
                      {log.record_id}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Changed Fields (for UPDATE) */}
            {log.action === "UPDATE" && log.changed_fields && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">
                  Changed Fields
                </h4>
                {renderChangedFields()}
              </div>
            )}

            {/* Old Data (for UPDATE and DELETE) */}
            {(log.action === "UPDATE" || log.action === "DELETE") &&
              log.old_data && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    Previous Data
                  </h4>
                  {renderJsonData(log.old_data)}
                </div>
              )}

            {/* New Data (for INSERT and UPDATE) */}
            {(log.action === "INSERT" || log.action === "UPDATE") &&
              log.new_data && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">New Data</h4>
                  {renderJsonData(log.new_data)}
                </div>
              )}

            {/* Additional Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">
                Additional Information
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">User Role:</span>
                  <span className="ml-2 text-gray-900">
                    {log.user_role || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Session ID:</span>
                  <span className="ml-2 text-gray-900 font-mono text-xs">
                    {log.session_id || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Transaction ID:</span>
                  <span className="ml-2 text-gray-900">
                    {log.transaction_id || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Log ID:</span>
                  <span className="ml-2 text-gray-900 font-mono text-xs">
                    {log.id}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-5 sm:mt-6">
            <button
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
